import React, { useEffect, useRef, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import { Drawer, Fab, alpha, useMediaQuery, useScrollTrigger } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
/** - icons */
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

/** Redux */
import { useDispatch, useSelector } from "react-redux";
import {
    getLeftDrawerOpen,
    setAreaForPanel,
    setLeftDrawerOpen,
    setLeftDrawerWidth,
} from "../../../_appStore/DB_Common";
import { getDatesForIndicators } from "../../../_appStore/DB_Dates";
import { getSelectedIndicators } from "../../../_appStore/DB_Indicators";
import { getJTsForIndicators } from "../../../_appStore/DB_JTs";

/** Helpers */
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import useHelper from "../../../_common/useHelper";

/** UI */
// import IndicatorsTree from "./IndicatorsTree";
import IndicatorsTree from "./IndicatorsXTree/IndicatorsXTree";
import AppTooltip from "@/src/_common/AppTooltip";
import SronlyH2 from "@/src/components/srOnly/SronlyH2";
import SearchTreeSkeleton from "./components/SearchTreeSkeleton";

const SearchTree = dynamic(() => import("./SearchTree"), { loading: () => <SearchTreeSkeleton /> });
const Resizer = dynamic(() => import("./components/Resizer"));
const StepsDialog = dynamic(() => import("../Steps/StepsDialog"));

const SwipeableLeftDrawer = (props) => {
    const { onSelectedIndicators } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { getAllIDs } = useHelper();

    const lgDown = useMediaQuery((theme) => theme.breakpoints.down("lg"));

    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 64,
    });

    const drawerRef = useRef(null);
    const searchRef = useRef();
    const menuBtnRef = useRef();

    /** Redux states */
    const leftDrawerOpen = useSelector(getLeftDrawerOpen);
    const selectedIndicators = useSelector(getSelectedIndicators);
    const dates4WSKs = useSelector(getDatesForIndicators);
    const JTs4WSKs = useSelector(getJTsForIndicators);

    /** States */
    const [CUSTOMER_FILTERS, setCUSTOMER_FILTERS] = useState({});
    const [DISABLED_CLOSE, setDISABLED_CLOSE] = useState(false);
    const [DRAWER_WIDTH, setDRAWER_WIDTH] = useState(false);
    const [OPEN, setOPEN] = useState(leftDrawerOpen);
    const [RESELECTED, setRESELECTED] = useState([]);
    const [SEARCH_DATA, setSEARCH_DATA] = useState([]);
    const [SEARCH_IDS, setSEARCH_IDS] = useState([]);

    /** Styles */
    const useStyles = makeStyles((theme) => ({
        drawerBackdrop: {
            position: "fixed",
            width: "100%",
            height: "100%",
            zIndex: 1,
            background: alpha(theme.palette.common.white, 0.005),
            marginTop: -16,
            pointerEvents: "none",
            [theme.breakpoints.down("lg")]: {
                background: alpha(theme.palette.common.white, 1),
            },
        },
        navbar: {
            // width: "calc(100% + 32px)",
            width: "calc(100% + 56px)",
            position: "absolute",
            top: 0,//theme.spacing(3.5),
            marginLeft: "-3px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            [theme.breakpoints.down("lg")]: {
                width: "calc(100% - 16px)",
                padding: theme.spacing(1, 1, 1, 2),
                marginTop: -24,
                marginLeft: 0,
            },
            [theme.breakpoints.down("md")]: {
                padding: theme.spacing(0, 0, 0, 0),
            },
        },
        navbarClosed: {
            width: "calc(100% + 52px)",
            [theme.breakpoints.down("lg")]: {
                top: 62,
                width: "calc(100% + 64px)",
            },
            [theme.breakpoints.down("md")]: {
                top: 96,
                width: "calc(100% + 54px)",
            },
            "@media (max-width: 750px)": {
                top: 86,
                width: "calc(100% + 58px)",
            },
            [theme.breakpoints.down("sm")]: {
                top: 52,
                width: "calc(100% + 46px)",
            },
            "@media (max-width: 319px)": {
                top: 24,
            },
        },
        btnNav: {
            width: 51,
            height: 51,
            marginTop: 0,//theme.spacing(0.5),
            "&:hover": {},
            "&:hover, &:focus": {},
            [theme.breakpoints.down("lg")]: {
                width: 40,
                height: 40,
                "& svg": {
                    fontSize: "1rem",
                },
            },
        },
        drawerRoot: {},
        drawerPaper: {
            marginTop: trigger ? 146 : 146,
            width: DRAWER_WIDTH,
            // height: "calc(100% + 65px)",
            // height: "calc(100% + 0px)",
            height: "auto",
            maxWidth: "100vw",
            overflowY: "visible",
            visibility: "visible!important",
            transform: `translateX(0px)`,
            // position: trigger ? "fixed" : "fixed",
            position: trigger ? "absolute" : "absolute",
            border: "none",
            paddingLeft: theme.spacing(5),
            [theme.breakpoints.down("lg")]: {
                marginTop: 80,
                marginLeft: 8,
                paddingTop: 40,
                paddingLeft: theme.spacing(1),
                width: DRAWER_WIDTH - 16,
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: 20,
            },
            [theme.breakpoints.down("md")]: {
                marginTop: 80,
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1),
            },
            [theme.breakpoints.down("sm")]: {
                marginTop: 80,
            },
        },
        drawerPaperClose: {
            position: trigger ? "fixed" : "fixed",
            transform: `translateX(-${DRAWER_WIDTH - 0}px)!important`,
            [theme.breakpoints.down("sm")]: {
                transform: `translateX(-${DRAWER_WIDTH - 8 - 0}px)!important`,
            },
        },
        boxedWrapper: {
            ...theme.shape.boxedWrapper,
            marginBottom: theme.spacing(4),
            padding: theme.spacing(3),
            position: "relative",
            [theme.breakpoints.down("lg")]: {
                border: "none",
                boxShadow: "none",
                padding: theme.spacing(2, 2, 2, 2),
                marginBottom: 0,
                backgroundColor: "transparent",
                marginTop: theme.spacing(3),
            },
            [theme.breakpoints.down("md")]: {
                marginTop: theme.spacing(2),
                padding: theme.spacing(1, 0, 1, 0),
            },
        },
        wrapper: {
            padding: theme.spacing(0.5, 0, 0.5, 2),
        },
        divider: {
            width: "calc(100% - 16px)",
        },
        Alert: {
            width: "calc(100% - 56px)",
        },
        AlertMessage: {
            padding: 0,
        },
    }));

    const classes = useStyles();
    /** /end Styles */

    /** Effects */
    /** - resize event listener */
    useEffect(() => {
        if (!drawerRef.current) return;

        const resizing = () =>
            setTimeout(() => {
                const _newWidth = window.innerWidth > 688 ? 688 : window.innerWidth;

                setDRAWER_WIDTH(_newWidth);
                dispatch(setLeftDrawerWidth(_newWidth));
            }, 123);

        window.addEventListener("resize", resizing);
        resizing();
        return () => window.removeEventListener("resize", resizing);
    }, [drawerRef]);

    /** - set OPEN component state */
    useEffect(() => {
        setOPEN(leftDrawerOpen);

        setTimeout(() => {
            const SCROLLBAR = document.querySelector("#appScroll > .os-viewport");
            SCROLLBAR?.scrollTo({ top: 0, behavior: "smooth" });

            const _skipMenu = document.getElementById("__skip-links-holder__");
            if (_skipMenu) {
                _skipMenu?.focus();
            }
        }, 1);
    }, [leftDrawerOpen]);

    /** - set whether drawer can be closed */
    useEffect(() => setDISABLED_CLOSE(!selectedIndicators.length), [selectedIndicators]);

    /** - set customer filters */
    useEffect(() => {
        setCUSTOMER_FILTERS({
            dates: RESELECTED.map((i) => i.indicator_id).some((r) => dates4WSKs.includes(r)),
            JTs: RESELECTED.map((i) => i.indicator_id).some((r) => JTs4WSKs.includes(r)),
        });
    }, [RESELECTED, dates4WSKs, JTs4WSKs]);

    /** - clear search */
    useEffect(() => {
        if (SEARCH_IDS.length && !leftDrawerOpen) {
            setSEARCH_IDS([]);
        }
    }, [leftDrawerOpen, SEARCH_IDS]);
    /** /end Effects */

    /** Handlers */
    const handleToggleDrawer = (open) => (event) => {
        if (event && event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
            return;
        }
        dispatch(setAreaForPanel(null));
        dispatch(setLeftDrawerOpen(open));
    };

    const handleOnSelection = ({ newSelection }) => {
        setRESELECTED(newSelection);
        onSelectedIndicators(newSelection);
    };

    const handleOnGetTreeData = ({ treeData }) => {
        const _flat = getAllIDs(treeData).map((f) => {
            delete f.children;
            return f;
        });
        setSEARCH_DATA(_flat);
    };

    const handleOnSearch = (args) => {
        if (JSON.stringify(args.ids) !== JSON.stringify(SEARCH_IDS)) {
            setSEARCH_IDS(args.ids);
        }
    };

    const handleOnResize = (args) => {
        const { width } = args;
        setDRAWER_WIDTH(width);
        dispatch(setLeftDrawerWidth(width));
    };

    return (
        <div>
            <React.Fragment>
                <Drawer
                    ref={drawerRef}
                    anchor={"left"}
                    // open={OPEN}
                    open={true}
                    variant={"permanent"}
                    classes={{
                        root: classes.drawerRoot,
                        paper: clsx(classes.drawerPaper, OPEN || classes.drawerPaperClose),
                    }}
                >
                    <div className={clsx(classes.navbar, OPEN || classes.navbarClosed)}>
                        {lgDown ? <StepsDialog reselected={RESELECTED} /> : <span />}

                        <AppTooltip
                            title={OPEN ? t("Zwiń panel wyboru") : t("Rozwiń panel wyboru")}
                            arrow
                            placement={OPEN ? "top-end" : "top-start"}
                            type={"dark"}
                            classes={{ tooltip: classes.tooltip }}
                        >
                            <span>
                                <Fab
                                    id={"db-left-panel-open-close-btn"}
                                    ref={menuBtnRef}
                                    onClick={handleToggleDrawer(!OPEN)}
                                    className={classes.btnNav}
                                    disabled={DISABLED_CLOSE}
                                    color={"primary"}
                                    aria-label={OPEN ? t("Zwiń panel wyboru") : t("Rozwiń panel wyboru")}
                                    sx={{ display: DISABLED_CLOSE ? "none" : "inline-flex" }}
                                >
                                    {!!OPEN ? (
                                        <ArrowBackIosNewIcon fontSize={"small"} />
                                    ) : (
                                        <ArrowForwardIosIcon fontSize={"small"} />
                                    )}
                                </Fab>
                            </span>
                        </AppTooltip>
                    </div>
                    {OPEN ? (
                        <>
                            <div className={classes.boxedWrapper}>
                                <SronlyH2>{t("Wyszukiwarka danych")}</SronlyH2>
                                <SearchTree
                                    searchData={SEARCH_DATA}
                                    onSearch={(args) => handleOnSearch(args)}
                                    ref={searchRef}
                                />

                                <SronlyH2>{t("Drzewko wyboru danych")}</SronlyH2>
                                <IndicatorsTree
                                    open={OPEN}
                                    onGetTreeData={(treeData) => handleOnGetTreeData({ treeData })}
                                    onSelection={(newSelection) => handleOnSelection({ newSelection })}
                                    customerFilters={CUSTOMER_FILTERS}
                                    searchIds={SEARCH_IDS}
                                    searchRef={searchRef}
                                />
                                <Resizer onResize={(args) => handleOnResize(args)} drawerWidth={DRAWER_WIDTH} />
                            </div>
                        </>
                    ) : (
                        ""
                    )}
                </Drawer>
                {!!OPEN && <div className={classes.drawerBackdrop} />}
            </React.Fragment>
        </div>
    );
};

export default SwipeableLeftDrawer;
